import { useEffect, useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import QRCode from "qrcode";
import { SEO } from "@/components/SEO";

/**
 * /partner-kit?ref=<code>[&promo=<CODE>] — what an organisation needs to send
 * its members to HealthPass (MAR-007): its link, two banners to paste into its
 * site, and a printable flyer with a QR code for a clinic's waiting room.
 *
 * Every link carries the organisation's code, so the visit and the sign-up are
 * counted under the partner channel and against its CRM record — a prospect's
 * code included, without credit (`classifyReferralCode`). `utm_campaign` tells
 * the banner from the flyer. A member code (`promo`) is a Stripe promotion code
 * created in the Dashboard; the page only prints it, Checkout accepts it.
 *
 * Not indexed: the page is sent to one organisation at a time.
 */

const SITE = "https://travelhealthpass.app";
const CODE = /^[a-z0-9][a-z0-9-]{1,40}$/;
const PROMO = /^[A-Z0-9]{3,20}$/;

const BANNERS = [
  { file: "healthpass-banner-728x90.svg", width: 728, height: 90 },
  { file: "healthpass-banner-300x250.svg", width: 300, height: 250 },
];

export function partnerLink(code: string, campaign: string): string {
  return `${SITE}/?ref=${code}&utm_campaign=${campaign}`;
}

export function bannerSnippet(
  code: string,
  banner: (typeof BANNERS)[number],
): string {
  return (
    `<a href="${partnerLink(code, "partner-banner")}" rel="noopener">` +
    `<img src="${SITE}/images/partner/${banner.file}" width="${banner.width}" height="${banner.height}" ` +
    `alt="HealthPass: your emergency health card for every trip. Create yours free."></a>`
  );
}

function CopyBox({ text }: { text: string }) {
  const [copied, setCopied] = useState(false);
  return (
    <div className="flex gap-2 items-start">
      <code className="flex-1 block bg-gray-100 border border-gray-200 rounded p-3 text-xs break-all">
        {text}
      </code>
      <button
        type="button"
        className="shrink-0 bg-blue-600 text-white text-sm font-semibold px-3 py-2 rounded hover:bg-blue-700"
        onClick={() => {
          void navigator.clipboard?.writeText(text).then(() => setCopied(true));
        }}
      >
        {copied ? "Copied" : "Copy"}
      </button>
    </div>
  );
}

export function PartnerKitPage() {
  const [params] = useSearchParams();
  const rawCode = (params.get("ref") ?? "").trim().toLowerCase();
  const rawPromo = (params.get("promo") ?? "").trim().toUpperCase();
  const code = CODE.test(rawCode) ? rawCode : null;
  const promo = PROMO.test(rawPromo) ? rawPromo : null;

  const flyerLink = useMemo(
    () => (code ? partnerLink(code, "partner-flyer") : null),
    [code],
  );
  const [qr, setQr] = useState<string | null>(null);
  useEffect(() => {
    if (!flyerLink) return;
    void QRCode.toDataURL(flyerLink, { margin: 1, width: 360 }).then(setQr);
  }, [flyerLink]);

  return (
    <div className="min-h-screen bg-gray-50">
      <SEO
        title="Partner kit"
        description="Banners, link and a printable flyer for organisations that recommend HealthPass to their members."
        url={`${SITE}/partner-kit`}
        robots="noindex, nofollow"
      />

      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 pt-28 pb-16 space-y-12 print:hidden">
        <header>
          <h1 className="text-3xl font-bold text-gray-900 mb-3">
            HealthPass partner kit
          </h1>
          <p className="text-gray-700">
            Everything you need to point your members to a free emergency health
            card: your own link, two banners for your website and a flyer to
            print. Members who arrive through these reach the free plan
            directly, and we can tell you how many signed up through you.
          </p>
        </header>

        {!code ? (
          <p className="bg-amber-50 border border-amber-200 rounded p-4 text-amber-900">
            This kit needs your organisation&apos;s code. Please use the link
            from our email, or write to partnerships@travelhealthpass.app.
          </p>
        ) : (
          <>
            {promo && (
              <section>
                <h2 className="text-xl font-bold text-gray-900 mb-2">
                  Your members&apos; code
                </h2>
                <p className="text-gray-700 mb-3">
                  Members enter it at checkout when they choose a paid plan.
                </p>
                <CopyBox text={promo} />
              </section>
            )}

            <section>
              <h2 className="text-xl font-bold text-gray-900 mb-2">
                Your link
              </h2>
              <p className="text-gray-700 mb-3">
                For a newsletter, a resource page or a social post.
              </p>
              <CopyBox text={`${SITE}/?ref=${code}`} />
            </section>

            <section className="space-y-6">
              <h2 className="text-xl font-bold text-gray-900">Banners</h2>
              {BANNERS.map((banner) => (
                <div key={banner.file} className="space-y-3">
                  <img
                    src={`/images/partner/${banner.file}`}
                    width={banner.width}
                    height={banner.height}
                    alt=""
                    className="max-w-full h-auto"
                  />
                  <CopyBox text={bannerSnippet(code, banner)} />
                </div>
              ))}
            </section>

            <section>
              <h2 className="text-xl font-bold text-gray-900 mb-2">
                Flyer for print
              </h2>
              <p className="text-gray-700 mb-3">
                One page with a QR code, for a waiting room, a travel clinic or
                an event.
              </p>
              <button
                type="button"
                className="bg-blue-600 text-white font-semibold px-5 py-2 rounded hover:bg-blue-700"
                onClick={() => window.print()}
              >
                Print the flyer
              </button>
            </section>
          </>
        )}
      </div>

      {code && (
        <article className="hidden print:block p-12 text-gray-900">
          <p className="text-2xl font-bold text-blue-700">HealthPass</p>
          <h1 className="text-4xl font-bold mt-8 leading-tight">
            Your emergency health card for every trip
          </h1>
          <p className="text-lg mt-6">
            Allergies, medicines, conditions and who to call, on one card with a
            QR code. A doctor or paramedic abroad scans it and reads it in
            English, German, French, Spanish or Portuguese, with no app and no
            login.
          </p>
          <ul className="text-lg mt-6 list-disc pl-6 space-y-1">
            <li>The emergency card is free</li>
            <li>Runs in the browser, nothing to install</li>
            <li>Encrypted in transit and at rest</li>
          </ul>
          {qr && (
            <img src={qr} alt="" width={240} height={240} className="mt-10" />
          )}
          <p className="text-lg font-semibold mt-4">
            Scan to create yours, or visit travelhealthpass.app
          </p>
          {promo && (
            <p className="text-lg mt-2">
              Members&apos; code for paid plans: <strong>{promo}</strong>
            </p>
          )}
        </article>
      )}
    </div>
  );
}
